/**
 * Threshold-aware variant of ComputedTotalTile, used in place of it
 * for the `sponsor.income.household_total_annual` field on the
 * Sponsor Financials section.
 *
 * The plain ComputedTotalTile is semantically neutral ("we auto-
 * calculated this"); the household-income field has meaning beyond
 * the raw sum — USCIS compares it directly to 125% × FPG to decide
 * whether Part 7 (assets) is required. Showing the number without
 * showing where it lands relative to the threshold leaves the user
 * to do USCIS's arithmetic in their head.
 *
 * Three visual states:
 *   - `meets`: green check + "clears the $X threshold for a
 *     household of N." The dedicated `IncomeThresholdSuccessTile`
 *     at the top of the section is the loud confirmation; this
 *     inline tile is the quiet re-confirmation near the inputs.
 *   - `falls_short`: amber warning + "short by $X" + next-step hint
 *     pointing at the two remediation paths (add co-filer income or
 *     fill out assets).
 *   - `neutral`: gray surface, no threshold copy. Used when income
 *     hasn't been entered yet or household size is unknown.
 */
import type { FieldDefinition } from '@inside-us-aos/domain';

import type { IIncomeThresholdState } from './incomeThreshold';
import styles from './HouseholdIncomeMeterTile.module.css';

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export interface IHouseholdIncomeMeterTileProps {
  /**
   * The catalog field — used for the displayed label and the
   * `data-computed-total` attribute so it slots in for the generic
   * ComputedTotalTile cleanly. Optional: the tile carries its own
   * default label when omitted.
   */
  field?: FieldDefinition;
  state: IIncomeThresholdState;
}

type TMeterStatus = 'meets' | 'short' | 'neutral';

function statusForState(state: IIncomeThresholdState): TMeterStatus {
  if (state.reason === 'meets') return 'meets';
  if (state.reason === 'falls_short') return 'short';
  return 'neutral';
}

export function HouseholdIncomeMeterTile(
  props: IHouseholdIncomeMeterTileProps,
): JSX.Element {
  const { field, state } = props;
  const status = statusForState(state);
  const label = field?.label ?? "Your household's combined annual income";
  const gap = Math.max(0, state.threshold - state.householdIncome);

  return (
    <div
      className={`${styles.tile} ${styles[`tile--${status}`]}`}
      data-household-income-meter
      data-meter-status={status}
      data-computed-total={field?.id ?? 'sponsor.income.household_total_annual'}
      role="status"
      aria-live="polite"
    >
      <div className={styles.tile__head}>
        <span className={styles.tile__label}>{label}</span>
        <span className={styles.tile__badge} aria-hidden="true">
          auto-calculated
        </span>
      </div>
      <div className={styles.tile__value}>
        {USD_FORMATTER.format(state.householdIncome)}
      </div>
      {status === 'meets' ? (
        <p className={styles.tile__meets}>
          <span className={styles.tile__icon} aria-hidden="true">
            ✓
          </span>
          Meets the{' '}
          <strong>{USD_FORMATTER.format(state.threshold)}</strong> threshold
          (125% of poverty for a household of {state.householdSize}).
        </p>
      ) : null}
      {status === 'short' ? (
        <div className={styles.tile__short}>
          <p className={styles.tile__shortLine}>
            <span className={styles.tile__icon} aria-hidden="true">
              ⚠
            </span>
            <span>
              Short by <strong>{USD_FORMATTER.format(gap)}</strong> of the{' '}
              <strong>{USD_FORMATTER.format(state.threshold)}</strong>{' '}
              threshold (125% of poverty for a household of{' '}
              {state.householdSize}).
            </span>
          </p>
          <p className={styles.tile__shortHint}>
            Either add a household member's income above, or use assets below
            to close the gap.
          </p>
        </div>
      ) : null}
    </div>
  );
}
